import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import type {
  Place,
  BusinessRecommendation,
  RecommendationResult,
  RecommendationPriority,
  ReferralDestination,
} from "@hsh/shared";
import {
  RECOMMENDATION_PRIORITY_COLORS,
  RECOMMENDATION_PRIORITY_LABELS,
  RECOMMENDATION_CATEGORY_ICONS,
  REFERRAL_DESTINATION_LABELS,
} from "@hsh/shared";

interface RecommendationsSectionProps {
  place: Place;
  isMobile?: boolean;
}

/**
 * Generate a fingerprint of data that affects recommendations.
 * When this changes, we need to re-fetch recommendations.
 */
function getPlaceDataFingerprint(place: Place): string {
  // Fields that affect recommendations and scoring
  return [
    place._id,
    place.googlePlaceId || "",
    place.companyNumber || "",
    place.score,
    place.googleRating,
    place.googleReviewCount,
    place.website || "",
    place.phone || "",
    place.companyAge,
    place.companyStatus || "",
    place.googleBusinessStatus || "",
    place.foodHygieneRating?.rating,
    // Use timestamps as strings (they indicate re-enrichment)
    place.googleEnrichedAt?.toString() || "",
    place.companiesHouseEnrichedAt?.toString() || "",
    place.foodHygieneEnrichedAt?.toString() || "",
    place.enrichedAt?.toString() || "",
  ].join("|");
}

// Priority indicator component
function PriorityBadge({ priority }: { priority: RecommendationPriority }) {
  const colorClass = RECOMMENDATION_PRIORITY_COLORS[priority];
  const label = RECOMMENDATION_PRIORITY_LABELS[priority];

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${colorClass}`}
    >
      {priority === "critical" && (
        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      )}
      {label}
    </span>
  );
}

// Single recommendation card
function RecommendationCard({
  recommendation,
  isExpanded,
  onToggle,
}: {
  recommendation: BusinessRecommendation;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const categoryIcon = RECOMMENDATION_CATEGORY_ICONS[recommendation.category];

  return (
    <div
      className={`border rounded-lg transition-all duration-200 ${
        recommendation.priority === "critical"
          ? "border-red-300 bg-red-50"
          : recommendation.priority === "high"
          ? "border-orange-200 bg-orange-50"
          : "border-gray-200 bg-white"
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full text-left p-4 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-inset rounded-lg"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <span className="text-xl flex-shrink-0">{categoryIcon}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h4 className="font-medium text-gray-900 text-sm">
                  {recommendation.title}
                </h4>
                <PriorityBadge priority={recommendation.priority} />
              </div>
              <p className="text-sm text-gray-600 line-clamp-2">
                {recommendation.description}
              </p>
            </div>
          </div>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${
              isExpanded ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-4">
          {/* Reasoning */}
          <div>
            <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Why This Matters
            </h5>
            <p className="text-sm text-gray-700">{recommendation.reasoning}</p>
          </div>

          {/* Suggested Action */}
          <div className="bg-primary-50 rounded-lg p-3 overflow-hidden">
            <h5 className="text-xs font-semibold text-primary-700 uppercase tracking-wide mb-1">
              Suggested Action
            </h5>
            <p className="text-sm text-primary-900 font-medium break-words">
              {recommendation.suggestedAction}
            </p>
          </div>

          {/* Talking Points */}
          {recommendation.talkingPoints &&
            recommendation.talkingPoints.length > 0 && (
              <div>
                <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  💬 Talking Points
                </h5>
                <ul className="space-y-1.5">
                  {recommendation.talkingPoints.map(
                    (point: string, idx: number) => (
                      <li
                        key={idx}
                        className="flex items-start gap-2 text-sm text-gray-700"
                      >
                        <span className="text-primary-500 mt-0.5 flex-shrink-0">
                          •
                        </span>
                        <span>{point}</span>
                      </li>
                    )
                  )}
                </ul>
              </div>
            )}

          {/* Suggested Referral */}
          {recommendation.suggestedReferral && (
            <div className="flex items-center gap-2 bg-purple-50 rounded-lg p-3">
              <span className="text-purple-600">↗️</span>
              <div>
                <h5 className="text-xs font-semibold text-purple-700">
                  Referral Opportunity
                </h5>
                <p className="text-sm text-purple-900">
                  Consider referring to{" "}
                  <strong>
                    {
                      REFERRAL_DESTINATION_LABELS[
                        recommendation.suggestedReferral as ReferralDestination
                      ]
                    }
                  </strong>
                </p>
              </div>
            </div>
          )}

          {/* Impact & Timeframe */}
          <div className="flex flex-wrap gap-3 text-xs text-gray-500">
            {recommendation.estimatedImpact && (
              <span className="flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
                {recommendation.estimatedImpact}
              </span>
            )}
            {recommendation.timeframe && (
              <span className="flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {recommendation.timeframe}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Loading skeleton
function LoadingSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="h-4 w-3/4 bg-gray-200 rounded"></div>
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-gray-200 rounded"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 w-1/2 bg-gray-200 rounded"></div>
                <div className="h-3 w-3/4 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Main component
export default function RecommendationsSection({
  place,
  isMobile = false,
}: RecommendationsSectionProps) {
  const [recommendations, setRecommendations] =
    useState<RecommendationResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [hasLoadedAI, setHasLoadedAI] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [showAllRecommendations, setShowAllRecommendations] = useState(false);

  // Create a fingerprint of data that affects recommendations
  // This will trigger re-fetch when googlePlaceId, companyNumber, score, etc. change
  const placeDataFingerprint = useMemo(
    () => getPlaceDataFingerprint(place),
    [place]
  );

  // Reset all state when the place changes (different place or same place with updated data)
  useEffect(() => {
    setRecommendations(null);
    setHasLoadedAI(false);
    setExpandedIds(new Set());
    setShowAllRecommendations(false);
    setError(null);
  }, [placeDataFingerprint]);

  // Fetch rule-based recommendations (fast, free)
  const fetchRecommendations = useCallback(async () => {
    if (!place._id) return;

    setIsLoading(true);
    setError(null);
    setHasLoadedAI(false);

    try {
      // Get rule-based recommendations only (fast, no API cost)
      const response = await api.getPlaceRecommendations(place._id, {
        quick: false, // Full rule-based, but no AI
        includeAI: false,
      });
      setRecommendations(response.data);
      setIsLoading(false);
    } catch (err) {
      console.error("Failed to fetch recommendations:", err);
      setError("Failed to load recommendations");
      setIsLoading(false);
    }
  }, [place._id, placeDataFingerprint]);

  // Fetch AI-enhanced recommendations on demand
  const fetchAIRecommendations = useCallback(async () => {
    if (!place._id || hasLoadedAI) return;

    setIsLoadingAI(true);
    try {
      const response = await api.getPlaceRecommendations(place._id, {
        includeAI: true,
      });
      setRecommendations(response.data);
      // Only mark as loaded if AI actually returned enhanced results
      if (response.data.aiGenerated) {
        setHasLoadedAI(true);
      }
    } catch (aiError) {
      console.warn("AI recommendations failed:", aiError);
    } finally {
      setIsLoadingAI(false);
    }
  }, [place._id, hasLoadedAI]);

  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div>
        <h3
          className={`font-medium text-gray-900 mb-3 ${
            isMobile ? "text-base" : ""
          }`}
        >
          Smart Recommendations
        </h3>
        <LoadingSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h3
          className={`font-medium text-gray-900 mb-3 ${
            isMobile ? "text-base" : ""
          }`}
        >
          Recommended Actions
        </h3>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {error}
          <button
            onClick={fetchRecommendations}
            className="ml-2 underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!recommendations || recommendations.recommendations.length === 0) {
    return (
      <div>
        <h3
          className={`font-medium text-gray-900 mb-3 ${
            isMobile ? "text-base" : ""
          }`}
        >
          Recommended Actions
        </h3>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-green-700">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span className="font-medium">All Good!</span>
          </div>
          <p className="text-sm text-green-600 mt-1">
            No urgent actions required. Focus on maintaining the relationship.
          </p>
        </div>
      </div>
    );
  }

  const criticalCount = recommendations.recommendations.filter(
    (r: BusinessRecommendation) => r.priority === "critical"
  ).length;
  const highCount = recommendations.recommendations.filter(
    (r: BusinessRecommendation) => r.priority === "high"
  ).length;

  // Show first 3 or all based on toggle
  const displayedRecommendations = showAllRecommendations
    ? recommendations.recommendations
    : recommendations.recommendations.slice(0, 3);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3
            className={`font-medium text-gray-900 ${
              isMobile ? "text-base" : ""
            }`}
          >
            Smart Recommendations
          </h3>
          {isLoadingAI && (
            <span className="flex items-center gap-1 text-xs text-primary-600">
              <svg
                className="animate-spin h-3 w-3"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              AI enhancing...
            </span>
          )}
          {recommendations.aiGenerated && !isLoadingAI && (
            <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">
              ✨ AI Enhanced
            </span>
          )}
        </div>
      </div>

      {/* Summary */}
      {recommendations.summary && (
        <div
          className={`mb-4 p-3 rounded-lg text-sm ${
            criticalCount > 0
              ? "bg-red-50 text-red-800 border border-red-200"
              : highCount > 0
              ? "bg-orange-50 text-orange-800 border border-orange-200"
              : "bg-gray-50 text-gray-700 border border-gray-200"
          }`}
        >
          <p className="font-medium">{recommendations.summary}</p>
        </div>
      )}

      {/* Engagement Tip */}
      {recommendations.engagementTip && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <span className="text-blue-600 text-lg">💡</span>
            <div>
              <h5 className="text-xs font-semibold text-blue-700 uppercase">
                Engagement Tip
              </h5>
              <p className="text-sm text-blue-800">
                {recommendations.engagementTip}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* AI Insights Button - only show if not already loaded */}
      {!recommendations.aiGenerated && !isLoadingAI && !hasLoadedAI && (
        <button
          onClick={fetchAIRecommendations}
          className="mb-4 w-full py-2.5 px-4 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white text-sm font-medium rounded-lg flex items-center justify-center gap-2 transition-all shadow-sm hover:shadow"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
          Get AI-Powered Insights
        </button>
      )}

      {/* Loading AI indicator */}
      {isLoadingAI && (
        <div className="mb-4 w-full py-2.5 px-4 bg-purple-50 border border-purple-200 text-purple-700 text-sm rounded-lg flex items-center justify-center gap-2">
          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          Analyzing with AI...
        </div>
      )}
      {/* Best Contact Time */}
      {recommendations.bestContactTime && (
        <div className="mb-4 flex items-center gap-2 text-sm text-gray-600">
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>{recommendations.bestContactTime}</span>
        </div>
      )}

      {/* Recommendations List */}
      <div className="space-y-3">
        {displayedRecommendations.map((rec: BusinessRecommendation) => (
          <RecommendationCard
            key={rec.id}
            recommendation={rec}
            isExpanded={expandedIds.has(rec.id)}
            onToggle={() => toggleExpanded(rec.id)}
          />
        ))}
      </div>

      {/* Show More / Less Button */}
      {recommendations.recommendations.length > 3 && (
        <button
          onClick={() => setShowAllRecommendations(!showAllRecommendations)}
          className="mt-3 w-full py-2 text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center justify-center gap-1"
        >
          {showAllRecommendations ? (
            <>
              Show Less
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 15l7-7 7 7"
                />
              </svg>
            </>
          ) : (
            <>
              Show {recommendations.recommendations.length - 3} More
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </>
          )}
        </button>
      )}
    </div>
  );
}
